// src/components/ClientsOnlineDashboard/components/ClientsTable.tsx

import React from "react";
import { 
  FaUserCircle, 
  FaUserTag, 
  FaUserCheck, 
  FaCircle, 
  FaClock, 
  FaEye, 
  FaTrash, 
  FaFilter,
  FaCheckSquare,
  FaSquare,
  FaEnvelope
} from "react-icons/fa";
import { ClientWithStatus } from "./MenuClickDashboard";
import { getConnectionStatusStyle, formatDate } from "../utils/helpers";
import "../styles/ClientsTable.css";

interface ClientsTableProps {
  clients: ClientWithStatus[];
  onUserClick: (id: number) => void;
  onDeleteClick?: (client: ClientWithStatus) => void;
  showCheckboxes?: boolean;
  selectedIds?: number[];
  onToggleSelect?: (id: number) => void;
  onToggleSelectAll?: () => void;
}

const ClientsTable: React.FC<ClientsTableProps> = ({
  clients,
  onUserClick,
  onDeleteClick,
  showCheckboxes = false,
  selectedIds = [],
  onToggleSelect,
  onToggleSelectAll
}) => {
  return (
    <div className="table-container">
      <table className="clients-table">
        <thead>
          <tr>
            {showCheckboxes && (
              <th className="checkbox-cell">
                <div 
                  className="checkbox-wrapper"
                  onClick={onToggleSelectAll}
                >
                  {selectedIds.length === clients.length && clients.length > 0 ? (
                    <FaCheckSquare color="white" size={18} />
                  ) : (
                    <FaSquare color="white" size={18} />
                  )}
                </div>
              </th>
            )}
            <th className={showCheckboxes ? "" : "first-cell"}>
              <div className="header-content">
                <FaUserCircle /> Utilisateur
              </div>
            </th>
            <th>
                <div className="header-content">
                 <FaEnvelope /> Email
                </div>
            </th>
            <th>
              <div className="header-content">
                <FaUserTag /> Type
              </div>
            </th>
            <th>
              <div className="header-content">
                <FaUserCheck /> Statut compte
              </div>
            </th>
            <th>
              <div className="header-content">
                <FaCircle /> Connexion
              </div>
            </th>
            <th>
              <div className="header-content">
                <FaClock /> Dernière activité
              </div>
            </th>
            <th className="last-cell">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {clients.map((client, index) => {
            const statusStyle = getConnectionStatusStyle(client.connectionStatus);
            const isSelected = selectedIds.includes(client.id);
            
            return (
              <tr 
                key={client.id} 
                className={`table-row ${index % 2 === 0 ? 'even' : 'odd'}`}
                onClick={() => onUserClick(client.id)}
              >
                {showCheckboxes && (
                  <td className="checkbox-cell">
                    <div 
                      className="checkbox-wrapper"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleSelect?.(client.id);
                      }}
                    >
                      {isSelected ? (
                        <FaCheckSquare color="#4bb0c0" size={18} />
                      ) : (
                        <FaSquare color="#9CA3AF" size={18} />
                      )}
                    </div>
                  </td>
                )}
                <td className="user-cell">
                  <div className="user-info">
                    {client.profilePicture ? (
                      <img 
                        src={
                          client.profilePicture 
                            ? `${process.env.REACT_APP_API_BASE}/${client.profilePicture}` 
                            : undefined
                        }
                        alt={client.fullName}
                        className="user-avatar"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <FaUserCircle color="#9CA3AF" size={24} />
                    )}
                    <div>
                      <div className="user-name">{client.fullName}</div>
                      <div className="user-id">#{client.id}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className="email-badge">
                    {client.email}
                  </span>
                </td>
                <td>
                  <span className="badge type-badge">
                    <FaUserTag size={10} style={{ marginRight: 4 }} />
                    {client.userType === 'personne' ? 'Personne' :
                     client.userType === 'entreprise' ? 'Entreprise' : 'Professionnel'}
                  </span>
                </td>
                <td>
                  <span className={`badge status-badge ${client.accountStatus}`}>
                    <FaCircle size={8}  style={{ marginRight: 4 }}/>
                    {client.accountStatus === 'active' ? 'Actif' :
                     client.accountStatus === 'waiting_validation' ? 'En attente' :
                     client.accountStatus === 'anonymous' ? 'Anonyme' : 'Inactif'}
                  </span>
                </td>
                <td>
                  <div className="connection-info">
                    <span 
                      className="badge connection-badge"
                      style={{
                        background: statusStyle.background,
                        color: statusStyle.color
                      }}
                    >
                      <FaCircle 
                        size={8} 
                         style={{ marginRight: 4 }}
                        className={client.connectionStatus === 'online' ? 'pulse' : ''}
                      />
                      {statusStyle.label}
                    </span>
                    {client.timestamps.lastLoginAgo && (
                      <span className="connection-time">
                        (connecté {client.timestamps.lastLoginAgo})
                      </span>
                    )}
                  </div>
                </td>
                <td className="activity-cell">
                  {client.timestamps.lastActivity ? (
                    <div className="activity-info">
                      <FaClock size={12} />
                      <div>
                        <div>{formatDate(client.timestamps.lastActivity)}</div>
                        <div className="activity-ago">
                          {client.timestamps.lastActivityAgo}
                        </div>
                      </div>
                    </div>
                  ) : '—'}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="details-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUserClick(client.id);
                      }}
                    >
                      <FaEye size={12} />
                      Détails
                    </button>
                    
                    {client.accountStatus === 'anonymous' && onDeleteClick && (
                      <button
                        className="delete-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteClick(client);
                        }}
                      >
                        <FaTrash size={12} />
                        Supprimer
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {clients.length === 0 && (
            <tr>
              <td colSpan={showCheckboxes ? 8 : 7} className="empty-state">
                <FaFilter size={24} className="empty-icon" />
                <div>Aucun client trouvé</div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ClientsTable;